import React, { useState } from 'react';

import { Check, Share2 } from 'lucide-react';

import { CourseMinifiedDTO } from '../../services/apiClient';

interface ShareCourseButtonProps {
  course: CourseMinifiedDTO;
  className?: string;
}

// A universal maps link rather than a bare "lat, lng" string. Google's `?api=1` search URL is the
// one format Android, iOS and desktop all open in whatever map app the person actually uses, so
// the person receiving the share gets a pin they can navigate to rather than two numbers they have
// to paste somewhere.
const mapsUrl = (latitude?: number, longitude?: number): string =>
  `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;

// Share the course's location. Built for a phone: navigator.share opens the OS share sheet, which
// is how you actually get a course to someone standing next to you.
//
// Four-step fallback, because navigator.share is not everywhere — it needs a secure context, and
// desktop Firefox does not have it at all:
//   1. navigator.share            — the OS sheet.
//   2. navigator.clipboard        — copy the link, confirmed inline for two seconds.
//   3. document.execCommand       — the same copy, for when the async API is *denied* rather than
//                                   absent. Deprecated, and kept anyway: writeText rejects with
//                                   NotAllowedError whenever the document is not focused or a
//                                   permissions policy blocks it, and the old path still works
//                                   there. Without it the button silently does nothing.
//   4. a two-second "Copy failed" — better than a control that appears inert.
//
// AbortError is swallowed on purpose: it is what the browser throws when the person dismisses the
// share sheet, which is a normal outcome and not a failure to report.
// Copy without the async Clipboard API. Selection-based, so it works under a user gesture even
// when writeText is denied. Off-screen rather than display:none — a hidden element cannot be
// selected, so the copy would silently produce an empty string.
const copyByExecCommand = (text: string): boolean => {
  const field = document.createElement('textarea');
  field.value = text;
  field.setAttribute('readonly', '');
  field.style.position = 'fixed';
  field.style.top = '-9999px';
  document.body.appendChild(field);

  try {
    field.select();
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    document.body.removeChild(field);
  }
};

type ShareState = 'idle' | 'copied' | 'failed';

const ShareCourseButton: React.FC<ShareCourseButtonProps> = ({
  course,
  className = '',
}) => {
  const [state, setState] = useState<ShareState>('idle');

  const canShare = typeof navigator !== 'undefined' && navigator.share != null;
  // execCommand is always worth a try, so the button is useful even with no async clipboard.
  const canCopy = typeof document !== 'undefined';

  // No coordinates means nothing to share. Every course on the map has them — ExploreQuery filters
  // on both being non-null — but the DTO types them as optional, so this is not dead code.
  if (course.latitude == null || course.longitude == null) {
    return null;
  }

  if (!canShare && !canCopy) {
    return null;
  }

  const url = mapsUrl(course.latitude, course.longitude);

  const handleShare = async (
    event: React.MouseEvent<HTMLButtonElement>,
  ): Promise<void> => {
    // The button sits inside or beside a "select this course" control on every surface that
    // renders it, so without this a share also re-selects the course and re-flies the camera.
    event.stopPropagation();

    if (canShare) {
      try {
        await navigator.share({
          title: course.name,
          text: `${course.name}${course.address ? `\n${course.address}` : ''}`,
          url,
        });
        return;
      } catch (error) {
        // Dismissing the sheet is not an error worth falling back from.
        if ((error as Error)?.name === 'AbortError') {
          return;
        }
      }
    }

    // writeText rejects, it does not return false — an unhandled rejection here left the button
    // inert and threw into the console. Caught, then retried the old way.
    let copied = false;
    if (navigator.clipboard?.writeText != null) {
      try {
        await navigator.clipboard.writeText(url);
        copied = true;
      } catch {
        copied = false;
      }
    }

    if (!copied) {
      copied = copyByExecCommand(url);
    }

    setState(copied ? 'copied' : 'failed');
    window.setTimeout(() => setState('idle'), 2000);
  };

  return (
    <button
      type="button"
      onClick={handleShare}
      aria-label={`Share the location of ${course.name}`}
      className={`flex items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-white/20 bg-white/5 px-2 py-2 text-[10px] font-semibold uppercase tracking-wide text-white/80 transition-colors hover:border-primary hover:text-primary active:scale-[0.98] ${className}`}
    >
      {state === 'copied' && (
        <>
          <Check className="h-3.5 w-3.5 text-primary" />
          Copied
        </>
      )}
      {state === 'failed' && <>Copy failed</>}
      {state === 'idle' && (
        <>
          <Share2 className="h-3.5 w-3.5" />
          Share
        </>
      )}
    </button>
  );
};

export default ShareCourseButton;
